import { useEffect, useRef, useState, type FormEvent } from "react";
import { ArrowRight, X } from "lucide-react";

type Status = "idle" | "sending" | "success" | "error";

export function ContactModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const lastFocusRef = useRef<HTMLElement | null>(null);

  // Reset on close so reopening shows the form fresh.
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setStatus("idle");
        setErrorMsg("");
      }, 250);
      return () => clearTimeout(t);
    }
    lastFocusRef.current = document.activeElement as HTMLElement | null;
    setTimeout(() => firstFieldRef.current?.focus(), 80);
    return undefined;
  }, [open]);

  // Esc + restore focus on close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      lastFocusRef.current?.focus?.();
    };
  }, [open, onClose]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    // Honeypot: real humans don't fill hidden fields. Bots do.
    if ((data.get("company") as string)?.trim()) {
      setStatus("success");
      return;
    }
    const payload = {
      name: (data.get("name") as string)?.trim(),
      email: (data.get("email") as string)?.trim(),
      topic: (data.get("topic") as string)?.trim(),
    };
    if (!payload.name || !payload.email) {
      setStatus("error");
      setErrorMsg("Name and email are both needed so we can reach you back.");
      return;
    }

    setStatus("sending");
    setErrorMsg("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Request failed (${res.status}).`);
      }
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err instanceof Error
          ? err.message
          : "Something went wrong on our end. Please try again, or email hello@automate828.com directly."
      );
    }
  };

  return (
    <div
      className={`modal-backdrop${open ? " open" : ""}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-hidden={!open}
    >
      <div className="modal" ref={dialogRef}>
        <button
          className="modal-close"
          onClick={onClose}
          aria-label="Close"
          type="button"
        >
          <X size={18} />
        </button>

        {status === "success" ? (
          <div className="modal-success">
            <div className="icon">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <h3>Got it — talk soon.</h3>
            <p>
              We read every message. You'll hear back within one business day,
              usually faster.
            </p>
            <button
              className="btn btn-primary"
              onClick={onClose}
              style={{ marginTop: 8 }}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <h3 id="modal-title">Book a free intro</h3>
            <p>
              30 minutes. No slides, no pitch. Tell us a little about what's
              slowing you down.
            </p>
            <form onSubmit={onSubmit} noValidate>
              <div className="field">
                <label htmlFor="f-name">Your name</label>
                <input
                  id="f-name"
                  name="name"
                  required
                  placeholder="Marisol Chen"
                  ref={firstFieldRef}
                  autoComplete="name"
                  disabled={status === "sending"}
                />
              </div>
              <div className="field">
                <label htmlFor="f-email">Work email</label>
                <input
                  id="f-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@yourbusiness.com"
                  autoComplete="email"
                  disabled={status === "sending"}
                />
              </div>
              <div className="field">
                <label htmlFor="f-topic">What's slowing you down?</label>
                <textarea
                  id="f-topic"
                  name="topic"
                  rows={3}
                  placeholder="The accounting export still ships in a Word doc…"
                  disabled={status === "sending"}
                />
              </div>

              {/* Honeypot: visually + accessibly hidden, never tab-reachable. */}
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  left: "-10000px",
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                }}
              >
                <label>
                  Company (leave empty)
                  <input
                    name="company"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </label>
              </div>

              {status === "error" && errorMsg ? (
                <p
                  role="alert"
                  style={{
                    color: "var(--petal-coral)",
                    fontSize: 13,
                    margin: "4px 0 8px",
                    lineHeight: 1.5,
                  }}
                >
                  {errorMsg}
                </p>
              ) : null}

              <button
                className="btn btn-primary"
                type="submit"
                disabled={status === "sending"}
                style={{
                  width: "100%",
                  justifyContent: "center",
                  marginTop: 8,
                }}
              >
                {status === "sending" ? "Sending…" : "Send it over"}
                {status !== "sending" ? <ArrowRight className="btn-arrow" size={16} /> : null}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
